import { supabase } from './supabase'

/**
 * Compare two draft data objects to check if they're identical
 * Normalizes the data for comparison (removes timestamps, etc.)
 */
const compareDraftData = (data1, data2) => {
  if (!data1 || !data2) return false
  
  // Create normalized copies for comparison
  const normalize = (data) => {
    const normalized = { ...data }
    // Remove metadata fields that don't affect content
    delete normalized.savedAt
    delete normalized.updated_at
    // Deep clone to avoid mutating original
    return JSON.parse(JSON.stringify(normalized))
  }
  
  const normalized1 = normalize(data1)
  const normalized2 = normalize(data2)
  
  // Compare as JSON strings (deep comparison)
  return JSON.stringify(normalized1) === JSON.stringify(normalized2)
}

/**
 * Save draft to server (for data recovery)
 * Only saves if content has changed from the last saved draft
 */
export const saveDraft = async (organizationId, userId, formType, formId, data) => {
  try {
    // form_id can be null for new forms, or a string ID
    // Convert to null if it's not a valid UUID
    let normalizedFormId = formId
    if (formId && !formId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      // If it's not a UUID, store it as a string in draft_data instead
      // and set form_id to null
      normalizedFormId = null
      if (!data.formId) {
        data.formId = formId // Store original formId in draft_data
      }
    }

    // Check if we have an existing draft with the same content
    const existingDraft = await loadDraft(organizationId, userId, formType, formId)
    
    // If draft exists and content is identical, skip saving (just update timestamp)
    if (existingDraft && existingDraft.draft_data) {
      const isIdentical = compareDraftData(existingDraft.draft_data, data)
      
      if (isIdentical) {
        // Content is identical - just update the timestamp to keep it fresh
        const { data: updatedDraft, error } = await supabase
          .from('form_drafts')
          .update({
            updated_at: new Date().toISOString(),
          })
          .eq('id', existingDraft.id)
          .select()
          .single()
        
        if (error) throw error
        return updatedDraft
      }
    }

    // Content has changed or no existing draft - save/update normally
    const { data: draft, error } = await supabase
      .from('form_drafts')
      .upsert(
        {
          organization_id: organizationId,
          user_id: userId,
          form_type: formType,
          form_id: normalizedFormId,
          draft_data: data,
          updated_at: new Date().toISOString(),
        },
        { 
          onConflict: 'organization_id,user_id,form_type,form_id',
          ignoreDuplicates: false
        }
      )
      .select()
      .single()

    if (error) throw error
    return draft
  } catch (error) {
    console.error('Error saving draft:', error)
    // Don't throw - draft saving should not break the app
    return null
  }
}

/**
 * Load draft from server
 */
export const loadDraft = async (organizationId, userId, formType, formId) => {
  try {
    if (!organizationId || !userId || !formType) {
      return null
    }

    // Handle both UUID and string formIds
    const isUUID = formId && formId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)
    
    let query = supabase
      .from('form_drafts')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('user_id', userId)
      .eq('form_type', formType)

    // If formId is not a UUID, query for null form_id (new forms)
    // We'll filter by draft_data.formId in the results
    if (formId && !isUUID) {
      query = query.is('form_id', null)
    } else if (formId && isUUID) {
      query = query.eq('form_id', formId)
    } else {
      // No formId provided, get all drafts for this form type
      query = query.is('form_id', null)
    }

    const { data, error } = await query.order('updated_at', { ascending: false })

    if (error) {
      // If error is "not found", return null (not an error)
      if (error.code === 'PGRST116') return null
      throw error
    }
    
    // If we searched by null form_id and have a string formId, filter by draft_data.formId
    if (formId && !isUUID && data) {
      const matchingDraft = data.find(d => d.draft_data?.formId === formId)
      return matchingDraft || null
    }
    
    // Return the most recent draft
    return data?.[0] || null
  } catch (error) {
    console.error('Error loading draft:', error)
    return null
  }
}

/**
 * Delete draft after successful save
 */
export const deleteDraft = async (organizationId, userId, formType, formId) => {
  try {
    if (!organizationId || !userId || !formType) {
      return
    }

    const isUUID = formId && formId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)
    
    if (formId && isUUID) {
      // Delete by UUID form_id
      await supabase
        .from('form_drafts')
        .delete()
        .eq('organization_id', organizationId)
        .eq('user_id', userId)
        .eq('form_type', formType)
        .eq('form_id', formId)
    } else if (formId && !isUUID) {
      // For string formIds, find the draft first, then delete by ID
      const draft = await loadDraft(organizationId, userId, formType, formId)
      if (draft) {
        await supabase
          .from('form_drafts')
          .delete()
          .eq('id', draft.id)
      }
    } else {
      // No formId, delete all null form_id drafts for this form type
      await supabase
        .from('form_drafts')
        .delete()
        .eq('organization_id', organizationId)
        .eq('user_id', userId)
        .eq('form_type', formType)
        .is('form_id', null)
    }
  } catch (error) {
    console.error('Error deleting draft:', error)
  }
}

/**
 * Auto-save draft periodically
 */
export const autoSaveDraft = (organizationId, userId, formType, formId, data, interval = 30000) => {
  let timeoutId

  const save = () => {
    saveDraft(organizationId, userId, formType, formId, data)
    timeoutId = setTimeout(save, interval)
  }

  save()

  return () => {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
  }
}

